import { MAX_UPLOAD_FILE_SIZE_BYTES } from "@repo/app-types/constants/shared";
import type { ReactNode } from "react";
import { useDropzone } from "react-dropzone";
import { useUploadKnowledgeBaseAttachmentMutation } from "../../../api/entities/knowledgeBase.js";

export const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "text/plain": [".txt"],
  "text/markdown": [".md"],
};

interface UseKnowledgeBaseDocumentsUploaderProps {
  knowledgeBaseId: string;
  onUploadSuccess: (upload: { id: string; name: string }) => void;
  onUploadError: (fileName: string, error: any) => void;
  isDisabled?: boolean;
}

export const useKnowledgeBaseDocumentsUploader = ({
  knowledgeBaseId,
  onUploadSuccess,
  onUploadError,
  isDisabled: externalIsDisabled = false,
}: UseKnowledgeBaseDocumentsUploaderProps) => {
  const [uploadAttachment, { isLoading: isUploading }] =
    useUploadKnowledgeBaseAttachmentMutation();

  const isDisabled = externalIsDisabled || isUploading;

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    disabled: isDisabled,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    multiple: true,
    onDropRejected: (rejectedFiles) => {
      for (const { file, errors } of rejectedFiles) {
        const error = errors[0]; // Take first error for simplicity
        if (error === undefined) {
          continue;
        }
        if (error.code === "file-too-large") {
          onUploadError(
            file.name,
            new Error(
              `File exceeds the ${MAX_UPLOAD_FILE_SIZE_BYTES / 1024 / 1024}MB size limit.`,
            ),
          );
        } else if (error.code === "file-invalid-type") {
          onUploadError(
            file.name,
            new Error(
              `Invalid file type. Accepted types: ${Object.values(ACCEPTED_FILE_TYPES).flat().join(", ")}`,
            ),
          );
        } else {
          onUploadError(file.name, new Error(error.message));
        }
      }
    },
    onDropAccepted: async (acceptedFiles) => {
      for (const file of acceptedFiles) {
        try {
          const attachments = await uploadAttachment({
            knowledgeBaseId,
            file,
          }).unwrap();
          // Get the most recently uploaded attachment (last in the array, ordered by created_at ascending)
          const uploadedAttachment = attachments[attachments.length - 1];
          if (uploadedAttachment) {
            onUploadSuccess({
              id: uploadedAttachment.id,
              name: file.name,
            });
          }
        } catch (err: any) {
          console.error(`Error uploading file ${file.name}:`, err);
          onUploadError(file.name, err);
        }
      }
    },
  });

  const uploadHiddenInput: ReactNode = <input {...getInputProps()} />;

  return {
    openFileDialog: open,
    dropzoneProps: {
      getRootProps,
      getInputProps,
      isDragActive,
      isDragAccept,
      isDragReject,
    },
    isUploading,
    uploadHiddenInput,
  };
};
